import React, { memo, useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import IpdTextField from "../../../utils/IpdTextField";
import FormField from "./FormField";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import GenerateAbha from "../../../../hlthtek-demo/components/GenerateAbha";
import VerifyAbha from "../../../../hlthtek-demo/components/VerifyAbha";
import {
  mapAbhaPatientMergeToPatient,
  mapHealthIdDetailsToPatient,
} from "../utils/mapHealthIdDetailsToPatient";
import { ADMISSION_COLORS as C } from "../constants/admissionTheme";
import { CONFIGURED_SECTION_IDS } from "../utils/sectionConfiguration";
import { useSectionConfig } from "../context/SectionConfigContext";

const S = CONFIGURED_SECTION_IDS;

function AbhaPanel({
  patient,
  updateField,
  mergePatient,
  resetPatient,
  setSelectedPatientIdForSearch,
  taggingPatientId,
  selectedPatientIdForSearch,
  selectedOpdReferralId,
  accumulateSavedAddresses,
}) {
  const [genOpen, setGenOpen] = useState(false);
  const [verOpen, setVerOpen] = useState(false);
  const [healthIdDetails, setHealthIdDetails] = useState(false);
  const [, setMultiplePatientData] = useState([]);
  const [selectedPartiallyMatchedPatient, setSelectedPartiallyMatchedPatient] = useState(null);
  const [patientForMobileSearch, setPatientForMobileSearch] = useState(null);
  const [, setTransactionIdForDataVerification] = useState(null);
  const [isAbdmEnabled, setIsAbdmEnabled] = useState(false);
  const { isFieldActive, isFieldMandatory, config } = useSectionConfig();

  const showAbhaAddress = !config || isFieldActive(S.personal, "abhaAddress");
  const showAbhaNumber = !config || isFieldActive(S.personal, "abhaNumber");

  useEffect(() => {
    const flag = sessionStorage.getItem("isAbdmEnabled");
    if (flag !== undefined && flag !== null) {
      try {
        setIsAbdmEnabled(JSON.parse(flag));
      } catch {
        setIsAbdmEnabled(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!selectedPatientIdForSearch && !selectedOpdReferralId) {
      setHealthIdDetails(false);
      setPatientForMobileSearch(null);
      setSelectedPartiallyMatchedPatient(null);
    }
  }, [selectedPatientIdForSearch, selectedOpdReferralId]);

  const patientDataLoaded = Boolean(selectedPatientIdForSearch || selectedOpdReferralId);

  const abhaFieldsDisabled = !isAbdmEnabled
    || Boolean(healthIdDetails)
    || patientDataLoaded
    || Boolean(patientForMobileSearch)
    || Boolean(selectedPartiallyMatchedPatient);

  useEffect(() => {
    if (!genOpen) return;
    setHealthIdDetails(false);
    setSelectedPartiallyMatchedPatient(null);
  }, [genOpen]);

  useEffect(() => {
    if (!verOpen) return;
    setSelectedPartiallyMatchedPatient(null);
  }, [verOpen]);

  const applyMappedPatient = useCallback((mapped) => {
    if (!mapped || !Object.keys(mapped).length) return;
    mergePatient(mapped);
    if (mapped.addresses?.some((a) => a.line1 || a.pincode)) {
      accumulateSavedAddresses?.(mapped.addresses);
    }
    const patientId = mapped.patient_id || mapped.uhid;
    if (patientId) setSelectedPatientIdForSearch?.(patientId);
  }, [mergePatient, accumulateSavedAddresses, setSelectedPatientIdForSearch]);

  useEffect(() => {
    if (!healthIdDetails || healthIdDetails === false) return;
    applyMappedPatient(mapHealthIdDetailsToPatient(healthIdDetails));
  }, [healthIdDetails, applyMappedPatient]);

  useEffect(() => {
    if (!selectedPartiallyMatchedPatient) return;
    applyMappedPatient(mapHealthIdDetailsToPatient(selectedPartiallyMatchedPatient));
  }, [selectedPartiallyMatchedPatient, applyMappedPatient]);

  const handleSetPatientData = useCallback((legacyPatient) => {
    if (legacyPatient?.patient_id) {
      setPatientForMobileSearch(legacyPatient);
    }
    applyMappedPatient(mapAbhaPatientMergeToPatient(legacyPatient));
  }, [applyMappedPatient]);

  const fetchPatientDetails = useCallback(() => {
    // GenerateAbha / VerifyAbha call this on modal close — same as RegisterPatient.js.
  }, []);

  const handleNavigateToGenerateAbha = useCallback(() => {
    setGenOpen(true);
  }, []);

  const handleCloseGenerate = useCallback(() => {
    setSelectedPartiallyMatchedPatient(null);
  }, []);

  const handleCloseVerify = useCallback(() => {
    setPatientForMobileSearch(null);
    setSelectedPartiallyMatchedPatient(null);
  }, []);

  return (
    <Card variant="outlined" sx={{ bgcolor: C.cardBg, borderColor: C.border }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <AutoAwesomeIcon sx={{ color: C.primary, fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={600} color={C.textPrimary}>ABHA</Typography>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-end" spacing={1} mb={2}>
          <Button size="small" variant="outlined" startIcon={<VerifiedUserIcon />} onClick={() => setVerOpen(true)}>
            Verify ABHA
          </Button>
          <Button size="small" variant="contained" startIcon={<AutoAwesomeIcon />} onClick={() => setGenOpen(true)}>
            Generate ABHA
          </Button>
        </Stack>

        <Grid container spacing={2}>
          {showAbhaAddress && (
            <Grid item xs={12} sm={6}>
              <FormField label="ABHA Address" required={config ? isFieldMandatory(S.personal, "abhaAddress") : false}>
                <IpdTextField
                  fullWidth
                  size="small"
                  className="abha-address-input"
                  name="abhaAddress"
                  placeholder="Enter ABHA Address"
                  value={patient.abhaAddress || ""}
                  onChange={(e) => updateField("abhaAddress", e.target.value)}
                  disabled={abhaFieldsDisabled}
                  inputProps={{ "aria-label": "ABHA Address" }}
                />
              </FormField>
            </Grid>
          )}
          {showAbhaNumber && (
            <Grid item xs={12} sm={6}>
              <FormField label="ABHA Number" required={config ? isFieldMandatory(S.personal, "abhaNumber") : false}>
                <IpdTextField
                  fullWidth
                  size="small"
                  className="abha-number-input"
                  name="abhaNumber"
                  placeholder="Enter ABHA Number"
                  value={patient.abhaNumber || ""}
                  onChange={(e) => updateField("abhaNumber", e.target.value)}
                  disabled={abhaFieldsDisabled}
                  inputProps={{ "aria-label": "ABHA Number" }}
                />
              </FormField>
            </Grid>
          )}
        </Grid>

        <GenerateAbha
          showGenerateModal={genOpen}
          setShowGenerateModal={setGenOpen}
          setHealthIdDetails={setHealthIdDetails}
          fetchPatientDetails={fetchPatientDetails}
          taggingPatientId={taggingPatientId}
          setMultiplePatientData={setMultiplePatientData}
          setSelectedPartiallyMatchedPatient={setSelectedPartiallyMatchedPatient}
          setPatientData={handleSetPatientData}
          resetForm={resetPatient}
          setTransactionIdForDataVerification={setTransactionIdForDataVerification}
          onClose={handleCloseGenerate}
        />

        <VerifyAbha
          showVerifyModal={verOpen}
          setShowVerifyModal={setVerOpen}
          setHealthIdDetails={setHealthIdDetails}
          fetchPatientDetails={fetchPatientDetails}
          taggingPatientId={taggingPatientId}
          setMultiplePatientData={setMultiplePatientData}
          setSelectedPartiallyMatchedPatient={setSelectedPartiallyMatchedPatient}
          setPatientData={handleSetPatientData}
          resetForm={resetPatient}
          setTransactionIdForDataVerification={setTransactionIdForDataVerification}
          onNavigateToGenerateAbha={handleNavigateToGenerateAbha}
          onClose={handleCloseVerify}
        />
      </CardContent>
    </Card>
  );
}

export default memo(AbhaPanel);
